// ProductListUK.js - Complete with linked (cascading) filters, Stock inline, View modal, Edit page nav, City-specific delete

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaSearch, FaEye, FaEdit, FaTrash, FaPlus, FaMinus, FaTimes, FaClipboardList, FaCity, FaChevronDown, FaCheck } from "react-icons/fa";
import { useCurrency } from "../../context/CurrencyContext";
import "./ProductList.css";

const API = "https://super-backend.hira-tech.co.uk/api/product";
const ORIGIN = "https://super-backend.hira-tech.co.uk";
const NO_IMAGE = "https://via.placeholder.com/70x70?text=No+Image";

// Last loaded product list of each shop (All Cities) is kept, so the list shows
// instantly on the next visit while fresh data loads in the background.
const PRODUCTLIST_CACHE_PREFIX = "productlist_cache_";

const readProductListCache = (id) => {
  if (!id) return null;
  try {
    const raw = localStorage.getItem(`${PRODUCTLIST_CACHE_PREFIX}${id}`);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeProductListCache = (id, data) => {
  if (!id) return;
  try {
    localStorage.setItem(`${PRODUCTLIST_CACHE_PREFIX}${id}`, JSON.stringify(data));
  } catch (e) {}
};

const arr = (d) =>
  Array.isArray(d) ? d : Array.isArray(d?.results) ? d.results : Array.isArray(d?.data) ? d.data : [];

const pid = (p) => p.product_Id ?? p.id ?? p.product_id;

const label = (value, keys) =>
  typeof value === "string"
    ? value
    : value
    ? keys.map((key) => value[key]).find(Boolean) || ""
    : "";

const getCity = (stock) => stock.city ?? stock.selected_city ?? "";
const getStock = (stock) => Number(stock.stock ?? stock.product_Stock ?? 0);

// ===== Linked filters: default ("All ...") values =====
const ALL_TYPES = "All Types";
const ALL_CATEGORIES = "All Categories";
const ALL_VARIATIONS = "All Variations";
const ALL_COMPANIES = "All Companies";
const ALL_BRANDS = "All Brands";

// Product ye sab selected filters se match karta hai? (skip = jis filter ki options nikal rahe hain wo chhod do)
const matchesDimensions = (product, f, skip) =>
  (skip === "type" || f.type === ALL_TYPES || product.type === f.type) &&
  (skip === "category" || f.category === ALL_CATEGORIES || product.category === f.category) &&
  (skip === "variation" || f.variation === ALL_VARIATIONS || product.variation === f.variation) &&
  (skip === "company" || f.company === ALL_COMPANIES || product.company === f.company) &&
  (skip === "brand" || f.brand === ALL_BRANDS || product.brand === f.brand);

function ProductList() {
  const location = useLocation();
  const navigate = useNavigate();
  const { formatPrice, currency, loading: currencyLoading } = useCurrency();

  const initialShopId = location.pathname.match(/\/shop\/(\d+)/)?.[1] || "";

  const [shopId, setShopId] = useState(initialShopId);
  const [shopName, setShopName] = useState("");
  const [cities, setCities] = useState([]);
  const [products, setProducts] = useState(() => readProductListCache(initialShopId) || []);
  const [loading, setLoading] = useState(() => !readProductListCache(initialShopId));
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const [search, setSearch] = useState("");
  const [city, setCity] = useState("All Cities");
  const [type, setType] = useState(ALL_TYPES);
  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [variation, setVariation] = useState(ALL_VARIATIONS);
  const [company, setCompany] = useState(ALL_COMPANIES);
  const [brand, setBrand] = useState(ALL_BRANDS);
  const [status, setStatus] = useState("All Stock Status");

  // City dropdown state
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const cityDropdownRef = useRef(null);

  const productsRequestRef = useRef(0);

  const [price, setPrice] = useState("");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [image, setImage] = useState("");
  const [viewProduct, setViewProduct] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteCity, setDeleteCity] = useState("");

  const notify = (message) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 3200);
  };

  const stockStatus = (stock) =>
    stock <= 0 ? "Out of Stock" : stock <= 10 ? "Low Stock" : "In Stock";

  const imageUrl = (path) =>
    !path ? NO_IMAGE : /^https?:\/\//i.test(path) ? path : `${ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;

  useEffect(() => {
    const match = location.pathname.match(/\/shop\/(\d+)/);
    if (match?.[1] && match[1] !== shopId) {
      // Show the saved list of the new shop immediately (if there is one)
      const cached = readProductListCache(match[1]);
      setProducts(cached || []);
      setLoading(!cached);
      setShopId(match[1]);
    }
  }, [location.pathname, shopId]);

  useEffect(() => {
    if (shopId) return;
    fetch(`${API}/shops/`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data) => {
        const shop = arr(data)[0];
        if (shop) {
          const id = shop.shop_id ?? shop.id;
          setShopName(shop.shop_name ?? shop.name ?? `Shop ${id}`);
          setShopId(String(id));
          navigate(`/shop/${id}/productlist`, { replace: true });
        }
      })
      .catch(() => setError("Could not load shops. Check the API connection."));
  }, [shopId, navigate]);

  // Close city dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(e.target)) {
        setShowCityDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  async function loadCities(id) {
    try {
      const response = await fetch(`${API}/shop-cities/?shop_id=${id}`);
      if (!response.ok) throw new Error();
      const data = await response.json();
      const cityList = Array.isArray(data?.cities)
        ? data.cities
        : arr(data).map((item) => typeof item === "string" ? item : item.city).filter(Boolean);
      setCities([...new Set(cityList)]);
    } catch {
      setCities([]);
    }
  }

  async function loadProducts() {
    if (!shopId) return;

    // Only the latest request is allowed to update the screen
    const requestId = ++productsRequestRef.current;

    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({ shop_id: shopId });
      if (city !== "All Cities") params.set("city", city);

      const response = await fetch(`${API}/products/?${params.toString()}`);
      if (requestId !== productsRequestRef.current) return;
      if (!response.ok) throw new Error(`Products request failed (${response.status}).`);
      const data = arr(await response.json());
      if (requestId !== productsRequestRef.current) return;

      const transformed = data.map((product) => {
        const gallery = product.imageGallery || product.image_gallery || {};
        const mainImage =
          product.main_image_url ||
          gallery.image1_url || gallery.image1 ||
          gallery.image2_url || gallery.image2 ||
          gallery.image3_url || gallery.image3 ||
          gallery.image4_url || gallery.image4;

        const cityStocks = Array.isArray(product.city_stocks)
          ? product.city_stocks.map((s) => ({ city: getCity(s), stock: getStock(s) }))
          : [];

        if (!cityStocks.length && getCity(product)) {
          cityStocks.push({ city: getCity(product), stock: getStock(product) });
        }

        const totalStock = cityStocks.length
          ? cityStocks.reduce((t, s) => t + s.stock, 0)
          : getStock(product);

        return {
          raw: product,
          id: pid(product),
          name: product.product_Name ?? product.name ?? "",
          sku: product.SKU ?? product.sku ?? "",
          image: imageUrl(mainImage),
          category: label(product.product_category, ["category_Name", "category_name"]) || "Uncategorized",
          type: label(product.type, ["type_name"]),
          variation: label(product.product_variation, ["variation_name"]),
          company: label(product.implant_company, ["company_name"]),
          brand: label(product.product_brand, ["brand_name"]) || "No Brand",
          price: Number(product.product_Price ?? product.price ?? 0),
          city: getCity(product) || "N/A",
          cityStocks,
          stock: totalStock,
          description: product.product_Description ?? "",
          size: product.size ?? "",
          reference: product.reference ?? "",
          lot_number: product.lot_number ?? "",
          pack: product.product_isSale ?? "Yes",
          rawMaterial: product.rawMaterial ?? "",
          features: product.features ?? "",
        };
      });

      setProducts(transformed);
      if (city === "All Cities") writeProductListCache(shopId, transformed);
      setPage(1);
    } catch (err) {
      if (requestId === productsRequestRef.current) {
        setError(err.message || "Could not load products.");
      }
    } finally {
      if (requestId === productsRequestRef.current) setLoading(false);
    }
  }

  useEffect(() => {
    if (shopId) {
      loadCities(shopId);
      loadProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, city]);

  // ============================================================
  // ✅ LINKED FILTERS
  // Har filter ki options sirf un products se bani hain jo BAQI chune
  // hue filters se match karte hain. Is tarah:
  //  - Type chuno -> Category / Variation / Company / Connection me sirf wahi options
  //  - Company chuno -> Connection me sirf usi company ke connections
  //  - Connection chuno -> Company me sirf usi connection ki company
  // ============================================================
  const options = useMemo(() => {
    const f = { type, category, variation, company, brand };
    const pick = (skip, key, exclude) =>
      [...new Set(
        products
          .filter((p) => matchesDimensions(p, f, skip))
          .map((p) => p[key])
          .filter((v) => v && v !== exclude)
      )].sort((a, b) => String(a).localeCompare(String(b)));

    return {
      types: pick("type", "type"),
      categories: pick("category", "category"),
      variations: pick("variation", "variation"),
      companies: pick("company", "company"),
      brands: pick("brand", "brand", "No Brand"),
    };
  }, [products, type, category, variation, company, brand]);

  // Agar ek filter badalne se doosra chuna hua option ab match nahi karta to wo khud clear ho jaye
  useEffect(() => {
    if (!products.length) return;
    if (type !== ALL_TYPES && !options.types.includes(type)) setType(ALL_TYPES);
    if (category !== ALL_CATEGORIES && !options.categories.includes(category)) setCategory(ALL_CATEGORIES);
    if (variation !== ALL_VARIATIONS && !options.variations.includes(variation)) setVariation(ALL_VARIATIONS);
    if (company !== ALL_COMPANIES && !options.companies.includes(company)) setCompany(ALL_COMPANIES);
    if (brand !== ALL_BRANDS && !options.brands.includes(brand)) setBrand(ALL_BRANDS);
  }, [options, products.length, type, category, variation, company, brand]);

  const cityStock = (product) =>
    city === "All Cities"
      ? product.stock
      : product.cityStocks.find((s) => s.city === city)?.stock ?? 0;

  const filtered = products.filter((product) => {
    const query = search.toLowerCase().trim();
    return (
      (!query || product.name.toLowerCase().includes(query) || product.sku.toLowerCase().includes(query)) &&
      matchesDimensions(product, { type, category, variation, company, brand }, null) &&
      (status === "All Stock Status" || stockStatus(cityStock(product)) === status)
    );
  });

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const current = filtered.slice((page - 1) * pageSize, page * pageSize);

  async function saveStock(product, newStock) {
    if (city === "All Cities") {
      notify("Choose a city first to change its stock.");
      return;
    }
    if (!Number.isInteger(newStock) || newStock < 0) {
      notify("Stock must be zero or a positive whole number.");
      return;
    }

    let response = await fetch(`${API}/products/${product.id}/city-stock/`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ city, stock: newStock }),
    });

    if (response.status === 404 || response.status === 405) {
      response = await fetch(`${API}/products/${product.id}/update-city-stock/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ city, stock: newStock }),
      });
    }

    if (!response.ok) {
      notify("City-wise stock API is not available yet. No stock was changed.");
      return;
    }

    await loadProducts();
    notify(`${product.name} stock updated for ${city}.`);
  }

  async function bulkPrice() {
    if (!selected.length || price === "" || !Number.isFinite(Number(price))) {
      notify("Select products and enter a valid price.");
      return;
    }

    const response = await fetch(`${API}/products/bulk-price-update/`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ product_ids: selected, new_price: Number(price) }),
    });

    if (!response.ok) {
      notify("Price update failed.");
      return;
    }

    setPrice("");
    setSelected([]);
    await loadProducts();
    notify("Selected prices updated.");
  }

  async function deleteCityStock() {
    if (!deleteTarget) return;

    if (deleteCity === "__ALL__" || deleteCity === "") {
      if (!deleteCity) {
        notify("Please select an option.");
        return;
      }
      try {
        const response = await fetch(`${API}/products/${deleteTarget.id}/delete/`, { method: "DELETE" });
        if (!response.ok) {
          notify("Failed to delete product.");
          return;
        }
        const deletedName = deleteTarget.name;
        setDeleteTarget(null);
        setDeleteCity("");
        await loadProducts();
        notify(`Product "${deletedName}" deleted completely.`);
      } catch {
        notify("Failed to delete product.");
      }
      return;
    }

    let response = await fetch(
      `${API}/products/${deleteTarget.id}/city-stock/?city=${encodeURIComponent(deleteCity)}`,
      { method: "DELETE" }
    );

    if (response.status === 404 || response.status === 405) {
      response = await fetch(
        `${API}/products/${deleteTarget.id}/delete-city-stock/?city=${encodeURIComponent(deleteCity)}`,
        { method: "DELETE" }
      );
    }

    if (!response.ok) {
      notify("City-specific delete API is not available. Product was not deleted.");
      return;
    }

    const deletedName = deleteTarget.name;
    const deletedCity = deleteCity;
    setDeleteTarget(null);
    setDeleteCity("");
    await loadProducts();
    notify(`Deleted ${deletedName} stock for ${deletedCity}.`);
  }

  const handleEditProduct = (product) => {
    navigate(`/shop/${shopId}/productlist/edit/${product.id}`, {
      state: { product: product.raw, shopId, shopName, from: "productlist" },
    });
  };

  const clearFilters = () => {
    setSearch("");
    setType(ALL_TYPES);
    setCategory(ALL_CATEGORIES);
    setVariation(ALL_VARIATIONS);
    setCompany(ALL_COMPANIES);
    setBrand(ALL_BRANDS);
    setStatus("All Stock Status");
    setCity("All Cities");
    setPage(1);
  };

  // Filter badalne par page 1 par wapas
  const onFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  return (
    <div className="products-management">
      {toast && (
        <div className="product-inline-toast">
          {toast}
          <button onClick={() => setToast("")}>×</button>
        </div>
      )}

      <div className="products-action-section">
        <div className="products-heading">
          <h2>
            <FaClipboardList className="products-heading-icon" />
            Products List
            {shopName && <span className="products-shop-name">— {shopName}</span>}
          </h2>
        </div>
        <div className="products-action-right">

          {/* ⭐ CITY FILTER — inline dropdown next to search */}
          <div className="city-filter-inline" ref={cityDropdownRef}>
            <button
              type="button"
              className={`city-filter-inline-btn ${city && city !== "All Cities" ? "active" : ""}`}
              onClick={() => setShowCityDropdown(!showCityDropdown)}
            >

              <span>{city && city !== "All Cities" ? city : "All Cities"}</span>
              <FaChevronDown className={`city-filter-inline-arrow ${showCityDropdown ? "rotated" : ""}`} />
            </button>

            {showCityDropdown && (
              <div className="city-filter-inline-dropdown">
                <button
                  type="button"
                  className={`city-filter-inline-item ${city === "All Cities" ? "selected" : ""}`}
                  onClick={() => { setCity("All Cities"); setShowCityDropdown(false); setPage(1); }}
                >
                  <span>All Cities</span>
                  {city === "All Cities" && <FaCheck />}
                </button>
                {cities.map((c, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`city-filter-inline-item ${city === c ? "selected" : ""}`}
                    onClick={() => { setCity(c); setShowCityDropdown(false); setPage(1); }}
                  >
                    <span>{c}</span>
                    {city === c && <FaCheck />}
                  </button>
                ))}
                {cities.length === 0 && <div className="city-filter-inline-empty">No cities available</div>}
              </div>
            )}
          </div>

          <div className="products-search-wrapper">
            <FaSearch className="products-search-icon" />
            <input
              className="products-search-input"
              value={search}
              placeholder="Search products..."
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
        </div>
      </div>

      <div className="filters-section">
        <div className="filters-label">Filters:</div>
        <div className="filters-row">
          {/* City moved to header — no longer here */}
          <select value={type} onChange={onFilterChange(setType)}>
            <option>{ALL_TYPES}</option>
            {options.types.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select value={category} onChange={onFilterChange(setCategory)}>
            <option>{ALL_CATEGORIES}</option>
            {options.categories.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select value={variation} onChange={onFilterChange(setVariation)}>
            <option>{ALL_VARIATIONS}</option>
            {options.variations.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select value={company} onChange={onFilterChange(setCompany)}>
            <option>{ALL_COMPANIES}</option>
            {options.companies.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select value={brand} onChange={onFilterChange(setBrand)}>
            <option>{ALL_BRANDS}</option>
            {options.brands.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select value={status} onChange={onFilterChange(setStatus)}>
            <option>All Stock Status</option>
            <option>In Stock</option>
            <option>Low Stock</option>
            <option>Out of Stock</option>
          </select>
        </div>
        <div className="clear-all-btn-container">
          <button className="clear-filters-btn" onClick={clearFilters}>Clear All</button>
          {city !== "All Cities" && <span className="active-city-label">City: {city}</span>}
        </div>
      </div>

      <div className="price-update-section">
        <input
          type="number"
          min="0"
          className="price-input"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder={`New price (${currency})`}
        />
        <button className="update-prices-btn" onClick={bulkPrice} disabled={!selected.length}>
          Update Selected Prices ({selected.length})
        </button>
      </div>

      {error && <div className="products-error">{error}</div>}

      <div className="products-table-container">
        <div className="products-table-wrapper">
          <table className="products-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={current.length > 0 && current.every((p) => selected.includes(p.id))}
                    onChange={() =>
                      setSelected((old) =>
                        current.every((p) => old.includes(p.id))
                          ? old.filter((id) => !current.some((p) => p.id === id))
                          : [...new Set([...old, ...current.map((p) => p.id)])]
                      )
                    }
                  />
                </th>
                <th>IMAGE</th>
                <th>PRODUCT NAME</th>
                <th>SKU</th>
                <th>CATEGORY</th>
                <th>BRAND</th>
                <th>PRICE ({currency})</th>
                <th>STOCK & CITY</th>
                <th>STATUS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {current.length ? (
                current.map((product) => {
                  const stock = cityStock(product);
                  return (
                    <tr key={product.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.includes(product.id)}
                          onChange={() =>
                            setSelected((old) =>
                              old.includes(product.id)
                                ? old.filter((id) => id !== product.id)
                                : [...old, product.id]
                            )
                          }
                        />
                      </td>
                      <td>
                        <img
                          src={product.image}
                          alt={product.name}
                          className="product-list-image"
                          onClick={() => setImage(product.image)}
                          onError={(e) => { e.currentTarget.src = NO_IMAGE; }}
                        />
                      </td>
                      <td><span className="products-name">{product.name}</span></td>
                      <td>{product.sku || "-"}</td>
                      <td>{product.category}</td>
                      <td>{product.brand}</td>
                      <td className="products-price-cell">{formatPrice(product.price)}</td>

                      <td>
                        <div className="stock-city-block">
                          {product.cityStocks.length > 0 ? (
                            product.cityStocks.map((cs, i) => (
                              <div key={i} className="stock-city-row">
                                <span className="stock-city-name">{cs.city}</span>
                                <span className="stock-city-qty">{cs.stock}</span>
                                {city !== "All Cities" && cs.city === city && (
                                  <span className="stock-city-actions">
                                    <button
                                      title="Decrease"
                                      disabled={cs.stock <= 0}
                                      onClick={() => saveStock(product, cs.stock - 1)}
                                    ><FaMinus /></button>
                                    <button
                                      title="Increase"
                                      onClick={() => saveStock(product, cs.stock + 1)}
                                    ><FaPlus /></button>
                                  </span>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="stock-city-row">
                              <span className="stock-city-name">-</span>
                              <span className="stock-city-qty">{stock}</span>
                            </div>
                          )}
                        </div>
                      </td>

                      <td>
                        <span className={`status-badge ${stockStatus(stock).toLowerCase().replace(/\s+/g, "-")}`}>
                          {stockStatus(stock)}
                        </span>
                      </td>

                      <td>
                        <div className="product-row-actions">
                          <button title="View" onClick={() => setViewProduct(product)}>
                            <FaEye />
                          </button>
                          <button title="Edit" onClick={() => handleEditProduct(product)}>
                            <FaEdit />
                          </button>
                          <button
                            title="Delete"
                            onClick={() => {
                              setDeleteTarget(product);
                              setDeleteCity(city === "All Cities" ? "" : city);
                            }}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="10" className="no-data-cell">
                    {/* Nothing is shown while the first load is running, so "No products found" never flashes */}
                    {loading ? "" : "No products found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pagination">
        <span className="table-info">
          Showing {filtered.length ? (page - 1) * pageSize + 1 : 0}–{Math.min(page * pageSize, filtered.length)} of {filtered.length}
        </span>
        <label className="rows-per-page">
          Rows
          <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
          </select>
        </label>
        <button disabled={page === 1} onClick={() => setPage((v) => v - 1)}>Previous</button>
        {Array.from({ length: pages }, (_, i) => (
          <button key={i} className={page === i + 1 ? "active" : ""} onClick={() => setPage(i + 1)}>
            {i + 1}
          </button>
        ))}
        <button disabled={page === pages} onClick={() => setPage((v) => v + 1)}>Next</button>
      </div>

      {/* Image modal */}
      {image && (
        <div className="image-modal-overlay" onClick={() => setImage("")}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={() => setImage("")}>×</button>
            <img className="image-modal-img" src={image} alt="Product" />
          </div>
        </div>
      )}

      {/* VIEW MODAL */}
      {viewProduct && (
        <div className="product-modal-overlay" onClick={() => setViewProduct(null)}>
          <div className="product-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="product-view-header">
              <h3>{viewProduct.name}</h3>
              <button className="view-modal-close" onClick={() => setViewProduct(null)}><FaTimes /></button>
            </div>

            <div className="product-view-body">
              <div className="product-view-image">
                <img src={viewProduct.image} alt={viewProduct.name} onError={(e) => { e.currentTarget.src = NO_IMAGE; }} />
              </div>

              <div className="product-view-details">
                <div className="view-detail-row"><span className="view-label">SKU</span><span className="view-value">{viewProduct.sku || "-"}</span></div>
                <div className="view-detail-row"><span className="view-label">Type</span><span className="view-value">{viewProduct.type || "-"}</span></div>
                <div className="view-detail-row"><span className="view-label">Category</span><span className="view-value">{viewProduct.category || "-"}</span></div>
                <div className="view-detail-row"><span className="view-label">Variation</span><span className="view-value">{viewProduct.variation || "-"}</span></div>
                <div className="view-detail-row"><span className="view-label">Implant Company</span><span className="view-value">{viewProduct.company || "-"}</span></div>
                <div className="view-detail-row"><span className="view-label">Brand</span><span className="view-value">{viewProduct.brand || "-"}</span></div>
                <div className="view-detail-row"><span className="view-label">Reference</span><span className="view-value">{viewProduct.reference || "-"}</span></div>
                <div className="view-detail-row"><span className="view-label">Price</span><span className="view-value price">{formatPrice(viewProduct.price)}</span></div>
                <div className="view-detail-row"><span className="view-label">Raw Material</span><span className="view-value">{viewProduct.rawMaterial || "-"}</span></div>
                <div className="view-detail-row"><span className="view-label">Features</span><span className="view-value">{viewProduct.features || "-"}</span></div>

                <div className="view-detail-row view-stock-row">
                  <span className="view-label">Stock by City</span>
                  <div className="view-stock-list">
                    {viewProduct.cityStocks.length > 0 ? (
                      viewProduct.cityStocks.map((cs, i) => (
                        <div key={i} className="view-stock-item">
                          <span className="stock-city-name">{cs.city}</span>
                          <span className="stock-city-qty">{cs.stock} units</span>
                        </div>
                      ))
                    ) : <span className="view-value">-</span>}
                  </div>
                </div>

                {viewProduct.description && (
                  <div className="view-detail-row view-desc-row">
                    <span className="view-label">Description</span>
                    <span className="view-value">{viewProduct.description}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="product-view-footer">
              <button className="secondary-btn" onClick={() => setViewProduct(null)}>Close</button>
              <button className="primary-btn" onClick={() => { setViewProduct(null); handleEditProduct(viewProduct); }}>
                <FaEdit /> Edit Product
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {deleteTarget && (
        <div className="product-modal-overlay" onClick={() => { setDeleteTarget(null); setDeleteCity(""); }}>
          <div className="product-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Product</h3>
            <p><strong>{deleteTarget.name}</strong></p>
            <p>Choose which city's stock you want to delete:</p>

            <select value={deleteCity} onChange={(e) => setDeleteCity(e.target.value)}>
              <option value="">-- Select an option --</option>
              {deleteTarget.cityStocks.map((cs, i) => (
                <option key={i} value={cs.city}>
                  {cs.city} ({cs.stock} units)
                </option>
              ))}
              <option value="__ALL__">🗑️ All Cities (Delete whole product)</option>
            </select>

            <div className="product-modal-actions">
              <button className="secondary-btn" onClick={() => { setDeleteTarget(null); setDeleteCity(""); }}>
                Cancel
              </button>
              <button className="danger-btn" disabled={!deleteCity} onClick={deleteCityStock}>
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductList;